import React from 'react'
import PropTypes from 'prop-types'
import { Link } from 'react-router-dom'
import { COMPLETED_STATUSES, MATCH_STATUS } from '../../../../utils/matchStatus'
import LeagueMatchesTable from './LeagueMatchesTable'

export default function LeagueHistoryCard({
  leagueData,
  isExpanded,
  toggleLeague,
  pid,
  leagueContext = null,
}) {
  const { league, matches, divisions } = leagueData
  // Only forward the partner navigation state for the specific league that has context
  const partnerNavigationState =
    leagueContext && Number(leagueContext.leagueId) === Number(league.id)
      ? {
          fromLeague: true,
          leagueId: leagueContext.leagueId,
          divisionId: leagueContext.divisionId,
        }
      : null
  const completedMatches = matches.filter((m) =>
    COMPLETED_STATUSES.has(m.status),
  )
  const scheduledMatches = matches.filter(
    (m) => m.status === MATCH_STATUS.SCHEDULED,
  )

  return (
    <div className="row mb-4">
      <div className="col-12">
        <div className="card border-0 shadow-sm">
          <div
            className="card-header bg-light border-0 league-header-clickable"
            onClick={() => toggleLeague(league.id)}
            role="button"
            tabIndex={0}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault()
                toggleLeague(league.id)
              }
            }}
            aria-expanded={isExpanded}
            aria-label={`${isExpanded ? 'Collapse' : 'Expand'} ${league.name}`}
          >
            <div className="row align-items-center g-2 league-history-header">
              <div className="col-auto text-center">
                <i
                  className={`bi bi-chevron-${isExpanded ? 'down' : 'right'} text-primary`}
                  style={{ fontSize: '1.25rem' }}
                  aria-hidden="true"
                ></i>
              </div>
              <div className="col league-history-title">
                <h4 className="mb-1">
                  <Link
                    to={`/leagues/${league.id}`}
                    className="text-decoration-none"
                    onClick={(e) => e.stopPropagation()}
                  >
                    {league.name}
                  </Link>
                </h4>
                <p className="mb-0 text-muted small">
                  {league.season && `${league.season} • `}
                  {league.league_type || 'League'}
                  {divisions.length > 0 &&
                    ` • ${divisions.length} Division${divisions.length > 1 ? 's' : ''}`}
                </p>
              </div>
              <div className="col-auto">
                <div className="league-history-stats">
                  <div className="league-history-stat">
                    <div className="fw-bold text-success">
                      {completedMatches.length}
                    </div>
                    <div className="small text-muted">Completed</div>
                  </div>
                  <div className="league-history-stat">
                    <div className="fw-bold text-warning">
                      {scheduledMatches.length}
                    </div>
                    <div className="small text-muted">Scheduled</div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {isExpanded && (
            <div className="card-body">
              <LeagueMatchesTable
                matches={matches}
                pid={pid}
                isDoubles={league.league_type?.toLowerCase() === 'doubles'}
                partnerNavigationState={partnerNavigationState}
              />
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

LeagueHistoryCard.propTypes = {
  leagueData: PropTypes.shape({
    league: PropTypes.shape({
      id: PropTypes.number.isRequired,
      name: PropTypes.string.isRequired,
      season: PropTypes.string,
      league_type: PropTypes.string,
    }).isRequired,
    matches: PropTypes.array.isRequired,
    divisions: PropTypes.array.isRequired,
  }).isRequired,
  isExpanded: PropTypes.bool.isRequired,
  toggleLeague: PropTypes.func.isRequired,
  pid: PropTypes.number.isRequired,
  leagueContext: PropTypes.shape({
    leagueId: PropTypes.number,
    divisionId: PropTypes.number,
  }),
}
